import { WebLayout } from 'components/Layout';
import { motion } from 'framer-motion';
import { Mail, Phone, Globe, Users, Briefcase } from 'react-feather';

// Animation Variants
const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
};

// Clean Heading with subtle accent underline
const SectionHeading = ({ children }) => (
  <motion.h2
    className="relative text-4xl sm:text-5xl font-extrabold text-white tracking-tight"
    initial="hidden"
    whileInView="visible"
    variants={fadeUp}
  >
    {children}
    <span className="block mt-2 h-[3px] w-24 bg-gradient-to-r from-[#F7931E] to-transparent"></span>
  </motion.h2>
);

export function Company() {
  return (
    <WebLayout>
      {/* Hero Section */}
      <section className="relative z-10 text-center py-28 ">
        <motion.h1
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="text-5xl sm:text-6xl font-extrabold text-white"
        >
          Company
        </motion.h1>
        <p className="mt-6 text-lg sm:text-xl text-gray-300 max-w-2xl mx-auto">
          “Fuel moves the world, LonaPay moves the money behind it.”
        </p>
      </section>

      {/* About Company */}
      <section className="max-w-6xl mx-auto px-6 sm:px-12 py-20">
        <SectionHeading>About Us</SectionHeading>
        <motion.p
          className="mt-6 text-gray-300 max-w-4xl leading-relaxed"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
        >
          We are a fintech platform focused on the oil & gas logistics sector in Africa. Our mission
          is to simplify fuel-linked financial transactions by connecting transport operators, fuel
          stations, and financial institutions in real-time. Through Web, Mobile, and USSD channels,
          we provide emergency fuel payments, credit-backed Gas Tokens, and data-driven operational
          tools.
        </motion.p>
      </section>

      {/* Mission & Vision */}
      <section className="bg-[#0e0e0e] py-20 px-6 sm:px-12">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 text-gray-300">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible">
            <SectionHeading>Our Mission</SectionHeading>
            <p className="mt-4 text-lg leading-relaxed">
              To become the financial backbone for Africa’s fuel mobility by enabling secure,
              instant, and scalable energy-linked payments and financing.
            </p>
          </motion.div>
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible">
            <SectionHeading>Our Vision</SectionHeading>
            <p className="mt-4 text-lg leading-relaxed">
              To be Africa’s #1 emergency financial infrastructure for the oil & gas sector,
              powering mobility and economic growth.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Core Values */}
      <section className="max-w-6xl mx-auto px-6 sm:px-12 py-20">
        <SectionHeading>Core Values</SectionHeading>
        <motion.div
          className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 text-gray-300"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
        >
          {[
            {
              title: 'Innovation',
              desc: 'Leveraging technology to simplify complex energy finance.',
            },
            { title: 'Trust', desc: 'Ensuring secure and transparent transactions for all users.' },
            {
              title: 'Impact',
              desc: 'Empowering transport operators and fueling economic growth.',
            },
            {
              title: 'Accessibility',
              desc: 'Bringing financial tools to urban and rural users alike.',
            },
          ].map((value, idx) => (
            <div
              key={idx}
              className="p-6 bg-[#111]/50 rounded-lg border border-[#F7931E]/20 shadow-md hover:shadow-[0_0_20px_rgba(247,147,30,0.4)] transition"
            >
              <div className="text-xl font-bold text-[#F7931E]">{value.title}</div>
              <p className="mt-3 text-sm text-gray-400">{value.desc}</p>
            </div>
          ))}
        </motion.div>
      </section>

      {/* Team Section */}
      <section className="bg-[#0e0e0e] py-20 px-6 sm:px-12">
        <SectionHeading>Our Leadership</SectionHeading>
        <motion.div
          className="mt-10 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 text-center text-gray-300"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
        >
          {[
            {
              name: 'Jonathan Mwebaze',
              title: 'Founder, Director',
              bio: 'Full-stack systems architect, specialized in payment and API integrations.',
            },
            {
              name: 'Lonnah Tumwesigye',
              title: 'Co-Founder, Director',
              bio: 'Cyber security expert, specialized in payment and API security.',
            },
            {
              name: 'Joshua Oola',
              title: 'COO',
              bio: 'Operations leader with deep logistics and supply chain expertise.',
            },
          ].map((member, idx) => (
            <div
              key={idx}
              className="p-6 bg-[#111]/60 rounded-lg border border-[#F7931E]/20 shadow hover:shadow-[0_0_20px_rgba(247,147,30,0.4)]"
            >
              <Users className="mx-auto text-[#F7931E]" size={40} />
              <div className="mt-4 text-xl font-bold">{member.name}</div>
              <div className="text-[#F7931E] text-sm font-semibold">{member.title}</div>
              <p className="mt-2 text-gray-400 text-sm">{member.bio}</p>
            </div>
          ))}
        </motion.div>
      </section>

      {/* Contact Section */}
      <section className="max-w-6xl mx-auto px-6 sm:px-12 py-20">
        <SectionHeading>Contact Us</SectionHeading>
        <motion.div
          className="mt-6 flex flex-col sm:flex-row gap-6 text-gray-300 text-lg"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
        >
          <div className="flex items-center gap-2">
            <Mail size={18} /> info@lonapay.org
          </div>
          <div className="flex items-center gap-2">
            <Phone size={18} /> +256 782 692271
          </div>
          <div className="flex items-center gap-2">
            <Globe size={18} /> www.lonapay.org
          </div>
        </motion.div>
      </section>
    </WebLayout>
  );
}
